import React from "react";
import { Helmet } from 'react-helmet-async';
import { useLocation } from 'react-router-dom';
import { BASE_URL } from "../services/config";
import { motion } from "framer-motion";

export const TermsPage: React.FC = () => {
  const location = useLocation();
  return (
    <section className="py-24 bg-background">
      <Helmet>
        <title>Условия использования / Публичная оферта — SAFONOVA</title>
        <meta name="description" content="Публичная оферта и условия использования сайта safonova-brand.ru. Условия покупки, доставки и возврата товаров." />
        <link rel="canonical" href={`${BASE_URL || ''}${location?.pathname || '/terms'}`} />
        <meta property="og:title" content="Условия использования / Публичная оферта — SAFONOVA" />
        <meta property="og:description" content="Публичная оферта и условия использования сайта safonova-brand.ru. Условия покупки, доставки и возврата товаров." />
        <meta property="og:type" content="article" />
        <meta property="og:url" content={`${BASE_URL || ''}${location?.pathname || '/terms'}`} />
        <meta property="og:image" content={`${BASE_URL || ''}/og-default.png`} />
      </Helmet>
      <div className="container mx-auto px-6">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.45 }}
          className="max-w-3xl mx-auto"
        >
          <header className="text-center mb-10">
            <h1 className="text-3xl md:text-4xl font-light mb-3">Публичная <span className="font-medium">оферта</span></h1>
            <p className="text-foreground/70">Последнее обновление: 06.11.2025</p>
          </header>

          <article className="prose prose-lg max-w-none text-foreground/80 space-y-8">
            <section id="definitions">
              <h2 className="text-xl font-medium mb-3">1. Общие положения</h2>
              <div className="space-y-3">
                <p>1.1. Настоящий документ является официальным предложением (публичной офертой) Индивидуального предпринимателя Сафонова Анна Сергеевна, ОГРНИП 324774600132375, ИНН 773618853593 (далее — «Продавец»), адресованным физическим и юридическим лицам (далее — «Покупатель»), заключить договор купли-продажи товаров дистанционным способом на условиях, указанных в настоящей Оферте.</p>
                <p>1.2. Оформление заказа на Сайте https://safonova-brand.ru означает полное согласие Покупателя с условиями Оферты.</p>
                <p>1.3. Настоящая Оферта действует без ограничения срока, пока размещена на Сайте.</p>
              </div>
            </section>

            <section id="definitions">
              <h2 className="text-xl font-medium mb-3">2. Определения и термины</h2>
              <div className="space-y-3">
                <p>2.1. В настоящей оферте термины имеют следующие значения:</p>
                <ul className="list-disc pl-6 space-y-1">
                  <li><strong>Оферта</strong> — публичное предложение Продавца заключить Договор на изложенных условиях.</li>
                  <li><strong>Акцепт</strong> — полное и безоговорочное принятие Покупателем условий Договора.</li>
                  <li><strong>Продавец</strong> — Индивидуальный предприниматель Сафонова Анна Сергеевна.</li>
                  <li><strong>Покупатель</strong> — физическое лицо, заключившее с Продавцом Договор.</li>
                  <li><strong>Товар</strong> — ароматическая продукция и сопутствующие товары, представленные в интернет‑магазине.</li>
                </ul>
              </div>
            </section>

            <section id="subject">
              <h2 className="text-xl font-medium mb-3">3. Предмет договора</h2>
              <p>3.1. Продавец обязуется передать в собственность Покупателю, а Покупатель обязуется оплатить и принять Товары, заказанные в интернет‑магазине.</p>
              <p>3.2. Продавец гарантирует права на реализацию Товаров.</p>
              <p>3.3. Продавец не несёт ответственности за ненадлежащее исполнение обязательств по Договору в случае, если это произошло по вине Покупателя.</p>
              <p>3.4. Характеристики, ассортимент и цена товара указаны на Сайте и могут быть изменены Продавцом без предварительного уведомления Покупателя до момента оформления заказа.</p>
            </section>

            <section id="ordering">
              <h2 className="text-xl font-medium mb-3">4. Оформление Заказа</h2>
              <div className="space-y-3">
                <p>4.1. Заказ оформляется через интернет‑магазин. При оформлении Покупатель предоставляет ФИО, адрес доставки, электронную почту и контактный телефон.</p>
                <p>4.2. Наименование, количество, артикул и цена указаны в корзине. После оформления Покупатель получает электронное подтверждение на указанный e‑mail.</p>
                <p>4.3. Покупатель несёт ответственность за достоверность предоставленных данных.</p>
                <p>4.4. Продавец оставляет за собой право отменить заказ в случае выявления ошибок в информации о товаре.</p>
                <p>4.5. Продавец вправе уточнять данные по телефону или email.</p>
              </div>
            </section>

            <section id="terms">
              <h2 className="text-xl font-medium mb-3">5. Сроки исполнения Заказа</h2>
              <p>5.1. Срок исполнения зависит от наличия Товаров на складе и времени обработки Заказа. В случае отсутствия части заказа Продавец уведомляет Покупателя и вправе аннулировать недоступные позиции.</p>
              <p>5.2. Заказ считается доставленным в момент передачи Покупателю и подтверждается подписью или иным способом, предусмотренным сервисом доставки.</p>
              <p>5.3. Продавец не несёт ответственности за задержки в доставке, вызванные обстоятельствами непреодолимой силы.</p>
            </section>

            <section id="payment">
              <h2 className="text-xl font-medium mb-3">6. Оплата Заказа</h2>
              <div className="space-y-3">
                <p>6.1. Оплата осуществляется в российских рублях. Доступные способы оплаты указаны на сайте.</p>
                <p>6.2. При безналичной оплате исполнение обязательства считается совершённым с момента зачисления средств на расчётный счёт Продавца.</p>
                <p>6.3. Продавец оставляет за собой право изменять цены — в этом случае Покупатель оповещается и может подтвердить или аннулировать заказ.</p>
              </div>
            </section>

            <section id="delivery">
              <h2 className="text-xl font-medium mb-3">7. Доставка товара</h2>
              <p>7.1. Доставка осуществляется по адресу и в сроки, согласованные при оформлении. Стоимость доставки определяется при оформлении заказа.</p>
              <p>7.2. Неявка Покупателя может рассматриваться как отказ от исполнения Договора.</p>
              <p>7.3. Риск случайной гибели или повреждения Товара переходит к Покупателю с момента его получения.</p>
            </section>

            <section id="returns">
              <h2 className="text-xl font-medium mb-3">8. Возврат товара</h2>
              <div className="space-y-3">
                <p>8.1. Покупатель вправе отказаться от товара до его передачи, а после передачи — в течение 14 дней, не считая дня покупки, при соблюдении условий возврата.</p>
                <p>8.2. Возврат средств осуществляется в срок, не превышающий 10 дней со дня предъявления требования, за исключением расходов на доставку возвращённого товара.</p>
                <p>8.3. Товар надлежащего качества не подлежит возврату или обмену, если он был в употреблении, нарушена его целостность или отсутствуют потребительские свойства.</p>
              </div>
            </section>

            <section id="liability">
              <h2 className="text-xl font-medium mb-3">9. Ответственность сторон</h2>
              <p>9.1. Стороны несут ответственность за неисполнение или ненадлежащее исполнение Договора в соответствии с действующим законодательством РФ.</p>
              <p>9.2. Продавец не несёт ответственности за последствия, вызванные неверно указанным адресом доставки Покупателем.</p>
              <p>9.3. В случае нарушения условий Договора стороны обязуются возместить друг другу убытки, причинённые таким нарушением.</p>
            </section>

            <section id="privacy">
              <h2 className="text-xl font-medium mb-3">10. Конфиденциальность и защита персональной информации</h2>
              <div className="space-y-3">
                <p>10.1. Продавец использует персональные данные Покупателя только для исполнения обязательств и обязуется не разглашать их третьим лицам, за исключением случаев, предусмотренных договором или законом.</p>
                <p>10.2. Продавец получает техническую информацию (IP‑адрес и прочее) для обеспечения работы сервиса; эти данные не используются для установления личности без необходимости.</p>
                <p>10.3. Продавец принимает меры для защиты персональных данных Покупателя от несанкционированного доступа и использования.</p>
              </div>
            </section>

            <section id="term">
              <h2 className="text-xl font-medium mb-3">11. Срок действия договора</h2>
              <p>11.1. Договор вступает в силу с момента оформления заказа и действует до полного исполнения сторонами своих обязательств.</p>
            </section>

            <section id="additional">
              <h2 className="text-xl font-medium mb-3">12. Дополнительные условия</h2>
              <div className="space-y-3">
                <p>12.1. Продавец вправе передавать свои права и обязанности третьим лицам.</p>
                <p>12.2. В случае технической недоступности сервиса Продавец может приостанавливать работу сайта, уведомив пользователей по возможности.</p>
                <p>12.3. Все споры решаются переговорным путём, а при недостижении соглашения — в суде в соответствии с законодательством РФ.</p>
                <p>12.4. Все материалы Сайта (дизайн, фото, тексты, рецептуры ароматов, товарные знаки Safonova) являются объектами интеллектуальной собственности Продавца.</p>
                <p>12.5. Копирование и использование материалов без согласия Продавца запрещено.</p>
              </div>
            </section>

            <section id="requisites">
              <h2 className="text-xl font-medium mb-3">13. Реквизиты Продавца</h2>
              <div className="space-y-2">
                <p>Индивидуальный предприниматель Сафонова Анна Сергеевна</p>
                <p>ИНН: 773618853593</p>
                <p>ОГРНИП: 324774600132375</p>
                <p>Юридический адрес: 119313, г. Москва, Ленинский пр-кт, д. 87</p>
                <p>Телефон: +7 (995) 881-02-47</p>
                <p>Email: info@safonova-brand.ru</p>
              </div>
            </section>
          </article>
        </motion.div>
      </div>
    </section>
  );
};